/**
 * The analysis catalog — what can be run, what it needs, and what the
 * visitor will get back.
 *
 * This replaces the old flat CATEGORIES/NEEDS tables in PitvisorWeb.js.
 * Two things changed:
 *
 *   1. Every analysis carries a `blurb`. The old UI showed 24 unexplained
 *      buttons; nobody could tell "Race Trace" from "Positions" without
 *      running both.
 *   2. `needs` is expressed as ordered steps rather than a bag of flags,
 *      so the form can say "pick a year first" instead of rendering six
 *      dead dropdowns.
 */

/**
 * Normalise an /inputs option list.
 *
 * Years, sessions, drivers and laps arrive as plain strings. Races arrive
 * as {name, location} so a relocated event is findable — in 2026 the
 * Bahrain Grand Prix ran at Sepang, and a dropdown that only said
 * "Bahrain Grand Prix" gave you nothing to search for if you were looking
 * for Malaysia. `v` stays the FastF1 event name, because that is what
 * every analysis hands back to fastf1.get_event().
 */
export const toOpts = (arr) => (arr || []).map((x) => {
  if (x && typeof x === 'object') {
    const name = String(x.name);
    return { v: name, l: x.location ? `${name} · ${x.location}` : name };
  }
  return { v: String(x), l: String(x) };
});

export const CATEGORIES = [
  {
    id: 'session',
    label: 'Session',
    blurb: 'One session, one question — results, fastest laps, the full lap list.',
    funcs: [
      { id: 'fastest', name: 'Fastest laps', blurb: 'Every driver’s best lap, ranked, with the gap to pole.', needs: ['year', 'race', 'session'] },
      { id: 'results', name: 'Results', blurb: 'Classification: position, driver, team, points, status.', needs: ['year', 'race', 'session'] },
      { id: 'laps', name: 'All laps', blurb: 'Lap times for chosen drivers across the whole session.', needs: ['year', 'race', 'session', 'drivers'] },
    ],
  },
  {
    id: 'telemetry',
    label: 'Telemetry',
    blurb: 'Car data — speed, throttle, braking, corner by corner.',
    funcs: [
      { id: 'time', name: 'Speed over time', blurb: 'Speed traces for chosen drivers, aligned on session time.', needs: ['year', 'race', 'session', 'drivers', 'optLap'] },
      { id: 'distance', name: 'Speed over distance', blurb: 'The same traces aligned on track distance instead of time.', needs: ['year', 'race', 'session', 'drivers', 'optLap'] },
      { id: 'delta', name: 'Delta', blurb: 'Two drivers side by side: speed traces plus the time gap.', needs: ['year', 'race', 'session', 'd1', 'd2', 'optLap1', 'optLap2'] },
      { id: 'telemetry', name: 'Full telemetry', blurb: 'Speed, RPM, gear, throttle, brake and DRS for two drivers, stacked.', needs: ['year', 'race', 'session', 'd1', 'd2', 'optLap1', 'optLap2'] },
      { id: 'cornering', name: 'Cornering', blurb: 'Where two drivers lift, brake and commit through a distance range.', needs: ['year', 'race', 'session', 'd1', 'd2', 'optLap1', 'optLap2', 'dist1', 'dist2'] },
    ],
  },
  {
    id: 'race',
    label: 'Race',
    blurb: 'How the Grand Prix actually unfolded.',
    funcs: [
      { id: 'strategy', name: 'Strategy', blurb: 'Tyre stints per driver — compounds, lengths, pit windows.', needs: ['year', 'race'] },
      { id: 'positions', name: 'Positions', blurb: 'Running order lap by lap, with safety-car periods shaded.', needs: ['year', 'race'] },
      { id: 'racetrace', name: 'Race trace', blurb: 'Cumulative time lost or gained to the leader, lap by lap.', needs: ['year', 'race', 'drivers'] },
      { id: 'tires', name: 'Tyre use', blurb: 'Which compound was on which lap — rendered as a chart image.', needs: ['year', 'race', 'session', 'lap'] },
    ],
  },
  {
    id: 'track',
    label: 'Track',
    blurb: 'The circuit itself — maps and where the time is found.',
    funcs: [
      { id: 'gear', name: 'Gear map', blurb: 'Gear selection laid out around a lap — chart image.', needs: ['year', 'race', 'session', 'optDriver', 'optLap'] },
      { id: 'speed', name: 'Speed map', blurb: 'Speed colouring around a lap — chart image.', needs: ['year', 'race', 'session', 'optDriver', 'optLap'] },
      { id: 'sectors', name: 'Sectors', blurb: 'Sector-by-sector comparison of two drivers — chart image.', needs: ['year', 'race', 'session', 'd1', 'd2', 'optLap1', 'optLap2'] },
    ],
  },
  {
    id: 'standings',
    label: 'Standings',
    blurb: 'Championship picture for a whole season.',
    funcs: [
      { id: 'drivers', name: 'Drivers', blurb: 'Driver standings for the season.', needs: ['year'] },
      { id: 'constructors', name: 'Constructors', blurb: 'Team standings for the season.', needs: ['year'] },
      { id: 'points', name: 'Points', blurb: 'Points scored at every round.', needs: ['year'] },
      { id: 'battles', name: 'Teammate battles', blurb: 'Team-mate qualifying head-to-head across the season.', needs: ['year', 'drivers'] },
    ],
  },
  {
    id: 'info',
    label: 'Info',
    blurb: 'Reference material — calendars, circuits, drivers.',
    funcs: [
      { id: 'schedule', name: 'Schedule', blurb: 'The full season calendar with dates and formats.', needs: ['year'] },
      { id: 'event', name: 'Event info', blurb: 'Dates, sessions and details for one weekend.', needs: ['year', 'race'] },
      { id: 'track', name: 'Track info', blurb: 'Circuit map, length, lap record and session times.', needs: ['year', 'race'] },
      { id: 'driverstats', name: 'Driver stats', blurb: 'Season and career numbers for one driver.', needs: ['year', 'singleDriver'] },
    ],
  },
];

export const ALL_FUNCS = CATEGORIES.flatMap(c => c.funcs.map(f => ({ ...f, category: c.id, categoryLabel: c.label })));

export const FUNC_BY_ID = Object.fromEntries(ALL_FUNCS.map(f => [f.id, f]));

/** Analyses that return JSON and are rendered as a chart in the browser. */
export const DATA_FUNCS = new Set([
  'fastest', 'results', 'schedule', 'event', 'laps', 'time', 'distance',
  'delta', 'telemetry', 'cornering', 'strategy', 'positions', 'racetrace',
  'battles', 'track', 'driverstats',
]);

export function isDataFunc(id) {
  return DATA_FUNCS.has(id);
}

/** Human sentence for what still blocks the Run button. */
export function missingStep(func, values) {
  if (!func) return 'Choose an analysis';
  for (const need of func.needs) {
    switch (need) {
      case 'year': if (!values.year) return 'Pick a year'; break;
      case 'race': if (!values.race) return 'Pick a grand prix'; break;
      case 'session': if (!values.session) return 'Pick a session'; break;
      case 'lap': if (values.lap == null || values.lap === '') return 'Pick a lap'; break;
      case 'drivers': if (!values.drivers?.length) return 'Pick at least one driver'; break;
      case 'singleDriver': if (!values.drivers?.length) return 'Pick a driver'; break;
      // d1 / d2 are deliberately NOT here: delta, telemetry, sectors and
      // cornering all fall back to the session's fastest lap server-side,
      // exactly as they did before the rebuild. The fields render, they
      // just never block the run.
      default: break;
    }
  }
  return null;
}

/**
 * Assemble the input_list the backend expects for a given analysis.
 *
 * The select values are strings, but the server compares `year` against an
 * int inside get_sess (`elif yr >= 2023`) — passing "2025" through
 * uncoerced raises `'>=' not supported between instances of 'str' and
 * 'int'`. Everything numeric is normalised here so callers can't forget.
 */
export function buildInputList(funcId, v) {
  return normalise(rawInputList(funcId, v));
}

function normalise(out) {
  const n = out;
  if (n.year != null && n.year !== '') n.year = Number(n.year);
  ['lap', 'lap1', 'lap2'].forEach((k) => {
    if (n[k] != null && n[k] !== '') n[k] = Number(n[k]);
  });
  if (n.race != null) n.race = String(n.race);
  if (n.session != null) n.session = String(n.session);
  return n;
}

function rawInputList(funcId, v) {
  switch (funcId) {
    case 'schedule': case 'drivers': case 'constructors': case 'points':
      return { year: v.year };
    case 'event': case 'strategy': case 'positions': case 'track':
      return { year: v.year, race: v.race };
    case 'fastest': case 'results':
      return { year: v.year, race: v.race, session: v.session };
    case 'tires':
      return { year: v.year, race: v.race, session: v.session, lap: v.lap };
    case 'racetrace':
      return { year: v.year, race: v.race, drivers: v.drivers };
    case 'laps':
      return { year: v.year, race: v.race, session: v.session, drivers: v.drivers };
    case 'gear': case 'speed':
      return { year: v.year, race: v.race, session: v.session, driver: v.driver || null, lap: v.lap || null };
    case 'time': case 'distance':
      return { year: v.year, race: v.race, session: v.session, drivers: v.drivers, lap: v.lap || null };
    case 'delta': case 'telemetry': case 'sectors':
      return { year: v.year, race: v.race, session: v.session, driver1: v.d1 || null, driver2: v.d2 || null, lap1: v.lap1 || null, lap2: v.lap2 || null };
    case 'cornering':
      return {
        year: v.year, race: v.race, session: v.session,
        driver1: v.d1 || null, driver2: v.d2 || null,
        lap1: v.lap1 || null, lap2: v.lap2 || null,
        dist1: v.dist1 === '' || v.dist1 == null ? null : Number(v.dist1),
        dist2: v.dist2 === '' || v.dist2 == null ? null : Number(v.dist2),
      };
    case 'battles': case 'driverstats':
      return { year: v.year, drivers: v.drivers };
    default:
      return {};
  }
}
